import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { ValidationError } from "../../errors";
import { attachment } from "../shared";
import { defineModel } from "../types";

const runwareVectorizeParams = z
  .object({
    vectorizeModel: z
      .enum(["recraft:1@1", "picsart:1@1"])
      .default("recraft:1@1"),
    attachments: z.array(attachment).optional(),
    prompt: z.string().default(""),
  })
  .strip();

type RunwareVectorizeParams = z.infer<typeof runwareVectorizeParams>;

export const runwareVectorize = defineModel({
  key: "runware-vectorize",
  stepType: "image",
  spec: {
    label: "Vectorize",
    description:
      "Convert raster images (PNG, JPG, WEBP) into clean, scalable vector graphics (SVG)",
    icon: "vectorize.svg",
    tags: ["image-edit", "vectorize"],
    features: { attachments: true, terminalOutput: true } as const,
    options: {
      attachments: [
        {
          type: "image",
          allowedFormats: ["image/jpeg", "image/webp", "image/png"],
          label: "Image to vectorize",
          slot: "input_image",
          group: "input",
          required: true,
        },
      ],
    },
    params: runwareVectorizeParams,
    info: "Vectorization converts raster images into scalable vector graphics (SVG).",
  },
  providers: [
    {
      provider: "runware",
      modelId: "recraft:1@1",
      taskType: "vectorize",
      estimateCost() {
        // Recraft Vectorize costs $0.04 per image
        return 0.04 * 1_000_000 * (1 + BRAND_MARKUP);
      },
      toPayload(params: RunwareVectorizeParams) {
        const inputImage = params.attachments?.find(
          (att: { slot: string }) => att.slot === "input_image",
        );

        if (!inputImage?.data) {
          throw new ValidationError("Please provide an image to vectorize.");
        }

        const vectorizeModelId = params.vectorizeModel ?? "recraft:1@1";

        return {
          model: vectorizeModelId,
          outputFormat: "SVG" as const,
          inputs: {
            image: inputImage.data,
          },
        };
      },
    },
  ],
});
